import React from "react";
import { Link } from "react-router-dom";
import { useTenant } from "@/lib/useTenant";
import { getLang } from "@/lib/i18n";

/**
 * GlobalFooter — single, restrained, full-width footer mounted once in
 * App.js below the routed page content. Inherits the dark industrial
 * tone of the rest of the platform — no white-on-light contrast break,
 * no marketing copy, no duplicate brand mentions, no orange. Reference:
 * forgedopshq.com.
 *
 * Tenant-aware: reads brand_name / tagline / owner_name / owner_website
 * from `useTenant()` so a white-label tenant can swap its own brand in
 * via the `tenant_config` MongoDB collection without rebuild.
 */
export default function GlobalFooter() {
  const year = new Date().getFullYear();
  const tenant = useTenant();
  const lang = getLang();
  const tagline = (lang === "es" ? tenant.tagline_es : tenant.tagline_en) || tenant.tagline_en;
  return (
    <footer
      className="border-t mt-auto print:hidden"
      style={{
        background: "#080808",
        borderColor: "#161616",
      }}
      data-testid="global-footer"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 sm:py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6">
        <div className="flex items-center gap-3">
          <img
            src={tenant.logo_emblem || "/forgedops-emblem.png"}
            alt=""
            aria-hidden
            className="h-6 w-6 opacity-90"
            loading="lazy"
          />
          <span
            className="font-mono text-[10px] uppercase tracking-[0.2em]"
            style={{ color: "#666" }}
            data-testid="global-footer-tagline"
          >
            {tagline}
          </span>
        </div>

        <div
          className="font-mono text-[10px] tracking-[0.15em]"
          style={{ color: "#555" }}
        >
          © {year}{" "}
          <span style={{ color: "#888" }} data-testid="global-footer-owner">
            {tenant.owner_name || "ForgedOps LLC"}
          </span>
          <span className="mx-2" style={{ color: "#333" }}>·</span>
          <a
            href={tenant.owner_website || "https://forgedopshq.com"}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-white transition-colors"
            style={{ color: "#888" }}
          >
            {(tenant.owner_website || "https://forgedopshq.com").replace(/^https?:\/\//, "")}
          </a>
          <span className="mx-2" style={{ color: "#333" }}>·</span>
          <Link
            to="/legal/terms"
            className="hover:text-white transition-colors"
            style={{ color: "#888" }}
          >
            Terms
          </Link>
          <span className="mx-2" style={{ color: "#333" }}>·</span>
          <Link
            to="/legal/privacy"
            className="hover:text-white transition-colors"
            style={{ color: "#888" }}
          >
            Privacy
          </Link>
        </div>
      </div>
    </footer>
  );
}
